import { useEffect, useRef, useState } from 'react';

import { TOOLBAR_PADDING, TOOLBAR_SECTION_GAP } from '../GeneratikTable.constants';

const HYSTERESIS = 24;

export interface UseToolbarLayoutOptions {
  leftButtonLabels: string[];
  rightButtonsCount: number;
  searchOpen: boolean;
  showSearch: boolean;
  showColumns: boolean;
  showSort: boolean;
  showGroup: boolean;
  showFilter: boolean;
  showViewToggle: boolean;
  showVariantToggle: boolean;
  sortOptionsCount: number;
  groupOptionsCount: number;
}

export interface UseToolbarLayoutReturn {
  toolbarRef: React.RefObject<HTMLDivElement | null>;
  collapseLeft: boolean;
  collapseMiddle: boolean;
}

const useToolbarLayout = (options: UseToolbarLayoutOptions): UseToolbarLayoutReturn => {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [toolbarWidth, setToolbarWidth] = useState(0);
  const [collapseLeft, setCollapseLeft] = useState(false);
  const [collapseMiddle, setCollapseMiddle] = useState(false);
  const prevCollapseLeft = useRef(false);
  const prevCollapseMiddle = useRef(false);
  // Saved expanded-state widths, used for un-collapse threshold (avoids re-estimation)
  const expandedLeftWidth = useRef(0);
  const expandedRightWidth = useRef(0);
  const leftButtonLabelsKey = options.leftButtonLabels.join(',');

  useEffect(() => {
    const element = toolbarRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setToolbarWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const element = toolbarRef.current;
    if (!element) return;

    const containerWidth = element.clientWidth - TOOLBAR_PADDING * 2;
    if (containerWidth <= 0) return;

    // scrollWidth reports full content width even when parent has overflow:hidden
    const leftSectionEl = element.children[0] as HTMLElement | undefined;
    const rightSectionEl = element.children[1] as HTMLElement | undefined;

    let nextLeft = prevCollapseLeft.current;
    let nextMiddle = prevCollapseMiddle.current;

    if (!prevCollapseMiddle.current && !prevCollapseLeft.current) {
      // Fully expanded: measure actual widths and check for overflow
      expandedLeftWidth.current = leftSectionEl?.scrollWidth ?? 0;
      expandedRightWidth.current = rightSectionEl?.scrollWidth ?? 0;

      const gap = expandedLeftWidth.current > 0 ? TOOLBAR_SECTION_GAP : 0;
      if (expandedLeftWidth.current + gap + expandedRightWidth.current > containerWidth) {
        nextMiddle = true;
      }
    } else {
      // In a collapsed state: use saved expanded widths for un-collapse decision
      const savedGap = expandedLeftWidth.current > 0 ? TOOLBAR_SECTION_GAP : 0;
      const savedExpandedTotal = expandedLeftWidth.current + savedGap + expandedRightWidth.current;

      if (containerWidth >= savedExpandedTotal + HYSTERESIS) {
        // Enough space to fully expand
        nextMiddle = false;
        nextLeft = false;
      } else if (prevCollapseMiddle.current && !prevCollapseLeft.current) {
        // Middle is collapsed but left is not yet: check if left also overflows
        const currentLeftWidth = leftSectionEl?.scrollWidth ?? 0;
        const currentRightWidth = rightSectionEl?.scrollWidth ?? 0;
        const currentGap = currentLeftWidth > 0 ? TOOLBAR_SECTION_GAP : 0;

        if (currentLeftWidth + currentGap + currentRightWidth > containerWidth) {
          nextLeft = true;
        }
      }
    }

    if (nextLeft !== prevCollapseLeft.current || nextMiddle !== prevCollapseMiddle.current) {
      prevCollapseLeft.current = nextLeft;
      prevCollapseMiddle.current = nextMiddle;
      setCollapseLeft(nextLeft);
      setCollapseMiddle(nextMiddle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- leftButtonLabelsKey is the stable key for the array; collapseMiddle/collapseLeft needed for post-collapse re-check
  }, [
    toolbarWidth,
    collapseMiddle,
    collapseLeft,
    options.searchOpen,
    leftButtonLabelsKey,
    options.rightButtonsCount,
    options.showSearch,
    options.showColumns,
    options.showSort,
    options.showGroup,
    options.showFilter,
    options.showViewToggle,
    options.showVariantToggle,
    options.sortOptionsCount,
    options.groupOptionsCount,
  ]);

  return { toolbarRef, collapseLeft, collapseMiddle };
};

export default useToolbarLayout;
